import { Button } from '@parrot-software/design-system';
import { LoaderCircle } from '@parrot-software/design-system/icons';
import { Trans, useTranslation } from 'react-i18next';

import { ChannelAwaitingCard } from '@identity/presentation/components/base';
import { useRequirementVerificationPolling, useVerificationLinkSender } from '@identity/presentation/hooks';
import { translateIdentityError } from '@identity/presentation/utils';

export type EmailAwaitingViewProps = {
  /** The address the link was sent to: what the user just typed, not what the account had. */
  email: string;
  /** Returns to the capture view. It also stops the wait. */
  onUseAnotherEmail: () => void;
  /** The backend now reports the email as verified. The caller refreshes the data. */
  onVerified: () => void;
};

/**
 * Waiting view of the email step.
 *
 * The address is shown in full: the user typed it seconds ago, so hiding characters protects nothing
 * and takes away what they need to spot the most common problem here, a typo.
 *
 * The way back to the capture view sits in the same row as the resend, because the person who needs
 * it is looking at the wrong address right above; below the fold it would only nudge them to resend
 * to the same wrong inbox.
 *
 * @param {EmailAwaitingViewProps} props - Component properties
 */
export const EmailAwaitingView = ({ email, onUseAnotherEmail, onVerified }: EmailAwaitingViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, send } = useVerificationLinkSender();
  const { hasPersistentNetworkError, isTimedOut, restart } = useRequirementVerificationPolling({
    onVerified,
    requirement: 'EMAIL',
    resetKey: 'EMAIL',
  });

  const resend = async () => {
    const result = await send({ channel: 'EMAIL', value: email, isResend: true });

    if (!result) return;

    // A resend invalidates the previous link, so the whole wait starts again. The destination did
    // not change, which is why it cannot go through the reset key.
    restart();
  };

  return (
    <ChannelAwaitingCard
      description={
        isTimedOut ? (
          t('verifyAccount.email.awaiting.timedOutBody')
        ) : (
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="identity:verifyAccount.email.awaiting.body"
            values={{ email }}
          />
        )
      }
      errorMessage={translateIdentityError(t, errorKey, errorRetryAfterSeconds)}
      hasPersistentNetworkError={hasPersistentNetworkError}
      isTimedOut={isTimedOut}
      networkNotice={t('verifyAccount.email.awaiting.networkNotice')}
      title={t('verifyAccount.email.awaiting.title')}
    >
      <Button
        className="w-full sm:w-auto sm:px-[15px]"
        disabled={isSending}
        onClick={resend}
        variant={isTimedOut ? 'default' : 'outline'}
      >
        {isSending && (
          <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="email-resend-spinner" />
        )}
        {t('verifyAccount.email.awaiting.resend')}
      </Button>

      <Button className="w-full sm:w-auto sm:px-[15px]" onClick={onUseAnotherEmail} variant="link">
        {t('verifyAccount.email.awaiting.useAnotherEmail')}
      </Button>
    </ChannelAwaitingCard>
  );
};
